import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./Avatar";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tag, X, Check, UserPlus } from "lucide-react";
import { toast } from "sonner";

export function VideoViewer({ video, isOwner, open, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [tagQuery, setTagQuery] = useState("");
  const [tagResults, setTagResults] = useState([]);
  const [showTagInput, setShowTagInput] = useState(false);

  useEffect(() => {
    if (!open || !video) return;
    setTagQuery(""); setTagResults([]); setShowTagInput(false);
    api.get(`/videos/${video.id}/tags`).then(({ data }) => setTags(data)).catch(() => setTags([]));
  }, [open, video]);

  useEffect(() => {
    if (!tagQuery.trim()) { setTagResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/users/search?q=${encodeURIComponent(tagQuery)}`); setTagResults(data); } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [tagQuery]);

  if (!video) return null;

  const addTag = async (u) => {
    try {
      const { data } = await api.post(`/videos/${video.id}/tag`, { user_id: u.id });
      setTags((prev) => [...prev, data]); setTagQuery(""); setTagResults([]);
      toast.success(data.status === "pending" ? `${u.name} will be tagged once they approve` : `Tagged ${u.name}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const respondTag = async (tag, action) => {
    try {
      await api.post(`/videos/tags/${tag.id}/respond`, { action });
      if (action === "approve") setTags((prev) => prev.map((t) => t.id === tag.id ? { ...t, status: "approved" } : t));
      else setTags((prev) => prev.filter((t) => t.id !== tag.id));
      toast.success(action === "approve" ? "Tag approved" : "Tag removed");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl bg-white border-4 border-black p-0 overflow-hidden" data-testid="video-viewer">
        <DialogTitle className="sr-only">Video</DialogTitle>
        <DialogDescription className="sr-only">Watch video and tags</DialogDescription>
        <div className="grid md:grid-cols-[1.4fr_1fr] max-h-[88dvh]">
          <div className="bg-black flex items-center justify-center">
            <video src={mediaUrl(video.url)} controls autoPlay className="w-full max-h-[50dvh] md:max-h-[88dvh] object-contain" />
          </div>
          <div className="flex flex-col max-h-[38dvh] md:max-h-[88dvh] bg-spotter-cream">
            <div className="p-3 border-b-2 border-black">
              <div className="flex items-center justify-between mb-1">
                <span className="font-heading font-bold text-spotter-maroon flex items-center gap-1.5 text-sm"><Tag className="w-4 h-4" />Tagged</span>
                {isOwner && (
                  <button data-testid="video-add-tag-toggle" onClick={() => setShowTagInput((v) => !v)} className="text-xs font-bold text-spotter-maroon inline-flex items-center gap-1 underline"><UserPlus className="w-3.5 h-3.5" />Tag people</button>
                )}
              </div>
              {isOwner && showTagInput && (
                <div className="relative mb-2">
                  <Input data-testid="video-tag-input" value={tagQuery} onChange={(e) => setTagQuery(e.target.value)} placeholder="Search members to tag…" className="border-2 border-black h-8 text-sm bg-white" />
                  {tagResults.length > 0 && (
                    <div className="absolute z-40 mt-1 w-full bg-white border-2 border-black rounded-lg overflow-hidden brand-shadow-sm">
                      {tagResults.slice(0, 6).map((u) => (
                        <button key={u.id} data-testid={`video-tag-result-${u.id}`} onClick={() => addTag(u)} className="w-full flex items-center gap-2 p-2 hover:bg-spotter-cream text-left">
                          <Avatar user={u} size="sm" /><span className="text-sm font-semibold">{u.name}</span><span className="text-xs text-spotter-maroon">@{u.username}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {tags.length === 0 ? <p className="text-xs text-spotter-maroon">No one tagged yet.</p> : (
                <div className="flex flex-wrap gap-1.5" data-testid="video-tags">
                  {tags.map((t) => {
                    const pending = t.status === "pending";
                    const canApprove = pending && t.tagged_user_id === user.id;
                    const canRemove = isOwner || t.tagged_user_id === user.id;
                    return (
                      <span key={t.id} data-testid={`video-tag-${t.id}`} className={`inline-flex items-center gap-1 border-2 border-black rounded-full pl-1 pr-2 py-0.5 text-xs font-bold ${pending ? "bg-yellow-200" : "bg-spotter-gold/30"}`}>
                        <button onClick={() => navigate(`/profile/${t.tagged_user_id}`)} className="hover:underline">@{t.user?.username}</button>
                        {pending && <span className="text-[9px] uppercase text-spotter-maroon/70">pending</span>}
                        {canApprove && <button data-testid={`video-tag-approve-${t.id}`} onClick={() => respondTag(t, "approve")} title="Approve"><Check className="w-3 h-3 text-green-700" /></button>}
                        {canRemove && <button data-testid={`video-tag-remove-${t.id}`} onClick={() => respondTag(t, "remove")} title="Remove"><X className="w-3 h-3" /></button>}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <p className="text-xs text-spotter-maroon">{isOwner ? "Tag your Pride in this video. If they require approval, the tag stays pending until they accept." : "Tags on this video."}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
